<!-- Editable Custom Field Renderer Template -->
<div *ngIf="form" [formGroup]="form" [ngClass]="getFieldClasses()" class="custom-field-wrapper">
  <!-- Read-only mode -->
  <div *ngIf="!isEditing" class="flex flex-col w-full shrink-0 break-words max-w-full min-w-0">
    <div class="mt-1 text-sm break-words max-w-full">
      <ng-container [ngSwitch]="field.type">
        <!-- Text and Textarea -->
        <span *ngSwitchCase="'text'" class="break-words">{{ getDisplayValue() }}</span>
        <span *ngSwitchCase="'textarea'" class="break-words">{{ getDisplayValue() }}</span>

        <!-- Number -->
        <span *ngSwitchCase="'number'" class="break-words">{{ getDisplayValue() }}</span>

        <!-- Date -->
        <span *ngSwitchCase="'date'" class="break-words">{{ getDisplayValue() }}</span>

        <!-- DateTime -->
        <span *ngSwitchCase="'datetime'" class="break-words">{{ getDisplayValue() }}</span>

        <!-- Select -->
        <span *ngSwitchCase="'select'" class="break-words">{{ getDisplayValue() }}</span>

        <!-- Boolean -->
        <span *ngSwitchCase="'boolean'" class="flex items-center gap-2 break-words">
          <mat-icon
            [svgIcon]="getBooleanIcon()"
            [ngClass]="getBooleanIconClass()"
            class="icon-size-4"
          ></mat-icon>
          {{ getDisplayValue() }}
        </span>

        <!-- Default -->
        <span *ngSwitchDefault class="break-words">{{ getDisplayValue() }}</span>
      </ng-container>
    </div>
  </div>

  <!-- Edit mode -->
  <div *ngIf="isEditing">
    <!-- Text Input Field -->
    <mat-form-field
      *ngIf="field.type === 'text'"
      [appearance]="getMatFieldAppearance()"
      class="w-full"
      [class.is-loading]="loadingState"
    >
      <mat-label class="text-primary">{{ getLabel() }}</mat-label>
      <input
        matInput
        [name]="field.name"
        [formControlName]="field.name"
        [placeholder]="getPlaceholder()"
        [type]="getInputType()"
        [style.direction]="getTextDirection()"
        [maxlength]="getMaxLength()"
        [readonly]="field.read_only"
        (input)="onValueChange($event.target.value); onInputChange($event)"
      />
      <mat-spinner *ngIf="loadingState" matSuffix [diameter]="20"></mat-spinner>
      <mat-error *ngIf="hasError()">{{ getErrorMessage() }}</mat-error>
    </mat-form-field>

    <!-- Character count indicator for text fields -->
    <div
      *ngIf="field.type === 'text' && getMaxLength()"
      class="text-xs mt-1"
      [ngClass]="{
        'text-gray-500': !isApproachingLimit() && !hasReachedLimit(),
        'text-orange-500': isApproachingLimit(),
        'text-red-500 font-medium': hasReachedLimit()
      }"
    >
      {{ getCharCountMessage() }}
    </div>

    <!-- Textarea Field -->
    <mat-form-field
      *ngIf="field.type === 'textarea'"
      [appearance]="getMatFieldAppearance()"
      class="w-full editing"
      [class.is-loading]="loadingState"
    >
      <mat-label class="text-primary">{{ getLabel() }}</mat-label>
      <textarea
        matInput
        [name]="field.name"
        [formControlName]="field.name"
        [placeholder]="getPlaceholder()"
        [style.direction]="getTextDirection()"
        [maxlength]="getMaxLength()"
        [readonly]="field.read_only"
        rows="4"
        cdkTextareaAutosize
        [cdkAutosizeMinRows]="4"
        [cdkAutosizeMaxRows]="8"
        (input)="onValueChange($event.target.value); onInputChange($event)"
      >
      </textarea>
      <mat-spinner *ngIf="loadingState" matSuffix [diameter]="20"></mat-spinner>
      <mat-error *ngIf="hasError()">{{ getErrorMessage() }}</mat-error>
    </mat-form-field>

    <!-- Character count indicator for textarea fields -->
    <div
      *ngIf="field.type === 'textarea' && getMaxLength()"
      class="text-xs mt-1"
      [ngClass]="{
        'text-gray-500': !isApproachingLimit() && !hasReachedLimit(),
        'text-orange-500': isApproachingLimit(),
        'text-red-500 font-medium': hasReachedLimit()
      }"
    >
      {{ getCharCountMessage() }}
    </div>

    <!-- Number Input Field -->
    <mat-form-field
      *ngIf="field.type === 'number'"
      [appearance]="getMatFieldAppearance()"
      class="w-full"
      [class.is-loading]="loadingState"
    >
      <mat-label class="text-primary">{{ getLabel() }}</mat-label>
      <input
        matInput
        [name]="field.name"
        [formControlName]="field.name"
        [placeholder]="getPlaceholder()"
        [type]="getInputType()"
        [style.direction]="getTextDirection()"
        [readonly]="field.read_only"
        (input)="onValueChange($event.target.value); onInputChange($event)"
      />
      <mat-spinner *ngIf="loadingState" matSuffix [diameter]="20"></mat-spinner>
      <mat-error *ngIf="hasError()">{{ getErrorMessage() }}</mat-error>
    </mat-form-field>

    <!-- Date Input Field -->
    <mat-form-field
      *ngIf="field.type === 'date'"
      [appearance]="getMatFieldAppearance()"
      class="w-full"
      [class.is-loading]="loadingState"
    >
      <mat-label class="text-primary">{{ getLabel() }}</mat-label>
      <input
        matInput
        [formControlName]="field.name"
        [placeholder]="getPlaceholder()"
        [type]="getInputType()"
        [readonly]="field.read_only"
        (change)="onDateChange($event); onValueChange($event.target.value)"
      />
      <mat-spinner *ngIf="loadingState" matSuffix [diameter]="20"></mat-spinner>
      <mat-error *ngIf="hasError()">{{ getErrorMessage() }}</mat-error>
    </mat-form-field>

    <!-- DateTime Input Field -->
    <mat-form-field
      *ngIf="field.type === 'datetime'"
      [appearance]="getMatFieldAppearance()"
      class="w-full"
      [class.is-loading]="loadingState"
    >
      <mat-label class="text-primary">{{ getLabel() }}</mat-label>
      <input
        matInput
        [name]="field.name"
        [formControlName]="field.name"
        [placeholder]="getPlaceholder()"
        [type]="getInputType()"
        [readonly]="field.read_only"
        (change)="onDateChange($event); onValueChange($event.target.value)"
      />
      <mat-spinner *ngIf="loadingState" matSuffix [diameter]="20"></mat-spinner>
      <mat-error *ngIf="hasError()">{{ getErrorMessage() }}</mat-error>
    </mat-form-field>

    <!-- Select Dropdown Field -->
    <mat-form-field
      *ngIf="field.type === 'select'"
      [appearance]="getMatFieldAppearance()"
      class="w-full"
      [class.is-loading]="loadingState"
    >
      <mat-label class="text-primary">{{ getLabel() }}</mat-label>
      <mat-select
        [name]="field.name"
        [formControlName]="field.name"
        (selectionChange)="onValueChange($event.value)"
      >
        <mat-option
          *ngFor="let option of field.options; trackBy: trackByOption"
          [value]="option.value"
        >
          {{ option.label }}
        </mat-option>
      </mat-select>
      <mat-spinner *ngIf="loadingState" matSuffix [diameter]="20"></mat-spinner>
      <mat-error *ngIf="hasError()">{{ getErrorMessage() }}</mat-error>
    </mat-form-field>

    <!-- Boolean/Checkbox Field -->
    <div *ngIf="field.type === 'boolean'" class="w-full">
      <div class="flex items-center gap-2">
        <mat-checkbox
          [name]="field.name"
          [formControlName]="field.name"
          color="primary"
          (change)="onValueChange($event.checked)"
        >
          {{ getLabel() }}
        </mat-checkbox>
        <mat-spinner *ngIf="loadingState" [diameter]="20"></mat-spinner>
      </div>
      <mat-error *ngIf="hasError()" class="block mt-1 text-sm text-warn">
        {{ getErrorMessage() }}
      </mat-error>
    </div>

    <!-- Unsupported Field Type -->
    <div
      *ngIf="
        !['text', 'textarea', 'number', 'date', 'datetime', 'select', 'boolean'].includes(
          field.type
        )
      "
      class="w-full p-4 bg-warn/10 border border-warn/20 rounded-lg"
    >
      <p class="text-warn text-sm">Unsupported field type: {{ field.type }}</p>
    </div>
  </div>
</div>

<!-- Fallback when form is not available -->
<div *ngIf="!form" [ngClass]="getFieldClasses()" class="custom-field-wrapper">
  <div class="flex flex-col">
    <div class="mt-1 text-sm">
      <span class="text-gray-500">{{ 'Form not available' | transloco }}</span>
    </div>
  </div>
</div>
